import { defer, forkJoin, iif, Observable, of, throwError } from "rxjs";
import { catchError, concatMap, map } from "rxjs/operators";
import { Pet, PetLight } from "../models/pet/pet";
import { PetLoad } from "../models/pet/pet.load";
import { isUndefined, omitBy, pickBy, trim } from "lodash-es";
import { PageRequest } from "src/app/support/page-request";
import { PetCreate } from "../models/pet/pet.create";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { UserNotify } from "src/app/support/notify-decorator";
import { PetUpdate } from "../models/pet/pet.update";
import { environment } from "src/environments/environment";
import { HttpClient } from "@angular/common/http";
import { PaginatedResult } from "src/app/support/paginated-result";
import { PetUpdateTags } from "../models/pet/pet.update-tags";
import { PetLoadTags } from "../models/pet/pet.load-tags";
import { inject, Injectable } from "@angular/core";
import { PetDelete } from "../models/pet/pet.delete";
import { API } from "src/app/backend/api";
import { Customer } from "src/app/backend/models/customer/customer";
import { sntTranslate } from "src/app/support/translate";
import { getCustomerNominative } from "src/app/support/customer";
import { AttachmentV2 } from "src/app/backend/models/attachment/attachment";
import { dateUtil } from "src/app/support/date-util";
import { stringRemoveNonAsciiChar } from "src/app/support/string";
import {
  patientExtend,
  patientLightExtend
} from "src/app/backend/api/api-extend/patient.extend";
import { isPlainObject } from "es-toolkit";

export type PatientBackendAddAttachmentParams = {
  id: string;
  name: string;
  storageId: string;
  contentStorageId?: string;
  type: string;
  signable: boolean;
  relatedTo?: Array<{ id: string; type: "recovery" }>;
};

@Injectable()
export class PetBackend {
  private readonly http = inject(HttpClient);

  load(request: PetLoad): Observable<Pet> {
    let id: string = request.id;

    let idOrObjectId: string | { id: string } = request.id;
    if (isPlainObject(idOrObjectId) && !!idOrObjectId.id) {
      id = idOrObjectId.id;
    }

    return this.http
      .get<Pet>(`${environment.snoots.api.directory}/patients/${id}`)
      .pipe(map(patientExtend));
  }

  loadLight(id: string): Observable<PetLight> {
    return this.http
      .get<Pet>(`${environment.snoots.api.directory}/patients/${id}`, {
        params: {
          light: "true"
        }
      })
      .pipe(
        map((patient: PetLight) => {
          return patientLightExtend(patient);
        })
      );
  }

  searchByExternalId(externalId: string): Observable<Pet[]> {
    if (!trim(externalId)) {
      return throwError("externalId undefined");
    }

    return this.http
      .get<PaginatedResult<Pet>>(
        `${environment.snoots.api.directory}/patients`,
        {
          params: {
            externalId
          }
        }
      )
      .pipe(map((result) => result.docs));
  }

  list(
    request: PageRequest & {
      select?: string | undefined;
    }
  ): Observable<PaginatedResult<Pet>> {
    return this.http
      .get<PaginatedResult<Pet>>(
        `${environment.snoots.api.directory}/patients`,
        {
          params: omitBy(
            {
              p: request.page,
              s: request.pageSize,
              ...request.filterValues,
              select: request.select
            },
            isUndefined
          )
        }
      )
      .pipe(
        map((response) => ({
          ...response,
          docs: response.docs.map(patientExtend)
        }))
      );
  }

  listLight(request: PageRequest): Observable<PaginatedResult<Pet>> {
    return this.http
      .get<PaginatedResult<Pet>>(
        `${environment.snoots.api.directory}/patients`,
        {
          params: omitBy(
            {
              p: request.page,
              s: request.pageSize,
              light: true,
              ...request.filterValues
            },
            isUndefined
          )
        }
      )
      .pipe(
        map((response) => ({
          ...response,
          docs: response.docs.map(patientExtend)
        }))
      );
  }

  @UserNotify({
    successMessage: marker("Paziente creato"),
    errorMessage: marker("Impossibile creare il paziente")
  })
  create(request: PetCreate): Observable<Pet> {
    return this.http
      .post<Pet>(API.DIRECTORY.PATIENTS.CREATE(), request)
      .pipe(map(patientExtend));
  }

  search(name: string): Observable<PaginatedResult<Pet>> {
    if (!name) {
      return of(<any>{ docs: [], totalDocs: 0 });
    }

    return this.http
      .get<PaginatedResult<Pet>>(
        `${environment.snoots.api.directory}/patients`,
        {
          params: {
            q: name
          }
        }
      )
      .pipe(
        map((response) => ({
          ...response,
          docs: response.docs.map(patientExtend)
        }))
      );
  }

  searchByQuery(request: {
    q?: string;
    customerName?: string;
    microchip?: string;
    tags?: string[];
    pageSize?: number;
    light?: boolean;
  }): Observable<Pet[]> {
    const query: string = trim(request.q || "");
    const customerName: string = trim(request.customerName || "");
    const microchip: string = trim(request.microchip || "");
    const tags: string[] = request.tags || [];
    const light: boolean = request.light !== false;

    if (!query && !customerName && !microchip && tags.length === 0) {
      return of([]);
    }

    return this.http
      .get<PaginatedResult<Pet>>(
        `${environment.snoots.api.directory}/patients`,
        {
          params: omitBy(
            {
              q: query || undefined,
              customerName: customerName || undefined,
              microchip: microchip || undefined,
              tags: tags.length > 0 ? tags.join(",") : undefined,
              s: request.pageSize,
              light: light ? true : undefined
            },
            isUndefined
          )
        }
      )
      .pipe(
        map((response) => (response.docs || []).map(patientExtend)),
        catchError(() => of([]))
      );
  }

  @UserNotify({
    successMessage: marker("Paziente aggiornato"),
    errorMessage: marker("Impossibile aggiornare il paziente")
  })
  update(request: Partial<PetUpdate>, id: string): Observable<void> {
    return this.http.patch<void>(
      `${environment.snoots.api.directory}/patients/${id}`,
      request
    );
  }

  @UserNotify({
    successMessage: marker("Paziente cancellato"),
    errorMessage: marker("Impossibile cancellare il paziente")
  })
  delete(request: PetDelete): Observable<string> {
    return this.http.delete<string>(
      `${environment.snoots.api.directory}/patients/${request.id}`
    );
  }

  @UserNotify({
    successMessage: marker("Peso registrato"),
    errorMessage: marker("Non è stato possibile registrare il peso")
  })
  weighing(id: string, weight: number, createdAt: string): Observable<void> {
    return this.http.post<void>(
      `${environment.snoots.api.directory}/patients/${id}/weighing`,
      {
        createdAt: createdAt,
        weight: weight
      }
    );
  }

  @UserNotify({
    successMessage: marker("Tag aggiornati"),
    errorMessage: marker("Non è stato possibile aggiungere il tag")
  })
  updateTags(id: string, request: PetUpdateTags): Observable<Pet> {
    return this.http.patch<Pet>(
      `${environment.snoots.api.directory}/patients/${id}`,
      request
    );
  }

  @UserNotify({
    successMessage: (patient: Pet, customer: Customer) =>
      sntTranslate(
        marker("customerName ora è il proprietario di patientName"),
        {
          patientName: patient.name,
          customerName: getCustomerNominative(customer)
        }
      ),
    errorMessage: marker("Impossibile eseguire l'operazione")
  })
  setOwner(patientId: string, customer: Customer): Observable<void> {
    return this.http.patch<void>(
      `${environment.snoots.api.directory}/patients/${patientId}`,
      {
        owner: customer.id
      }
    );
  }

  @UserNotify({
    successMessage: (patient: Pet) =>
      sntTranslate(marker("patientName ora è senza proprietario"), {
        patientName: patient.name
      }),
    errorMessage: marker("Non è stato possibile aggiungere il tag")
  })
  unsetOwner(patient: Pet): Observable<void> {
    return this.http.patch<void>(
      `${environment.snoots.api.directory}/patients/${patient.id}`,
      {
        owner: null
      }
    );
  }

  @UserNotify({
    successMessage: marker("Contatto aggiunto"),
    errorMessage: marker("Non è stato possibile aggiungere il contatto")
  })
  addRelative(patientId: string, customerId: string): Observable<void> {
    return this.http.post<void>(
      `${environment.snoots.api.directory}/patients/${patientId}/relatives/${customerId}`,
      {}
    );
  }

  @UserNotify({
    successMessage: marker("Contatto aggiunto"),
    errorMessage: marker("Non è stato possibile aggiungere il contatto")
  })
  removeRelative(patientId: string, customerId: string): Observable<void> {
    return this.http.delete<void>(
      `${environment.snoots.api.directory}/patients/${patientId}/relatives/${customerId}`
    );
  }

  @UserNotify({
    successMessage: marker("Paziente aggiornato"),
    errorMessage: marker("Non è stato possibile aggiornare il paziente")
  })
  updateDataAndRelatives(
    patientId: string,
    payload: Partial<PetUpdate>,
    addedRelatives: string[] | undefined,
    removedRelatives: string[] | undefined
  ): Observable<unknown> {
    const add = iif(
      () => !!addedRelatives && addedRelatives.length > 0,
      defer(() =>
        forkJoin(
          addedRelatives!.map((id) =>
            this.http.post<void>(
              `${environment.snoots.api.directory}/patients/${patientId}/relatives/${id}`,
              {}
            )
          )
        )
      ),
      of(undefined)
    );

    const remove = iif(
      () => !!removedRelatives && removedRelatives.length > 0,
      defer(() =>
        forkJoin(
          removedRelatives!.map((id) =>
            this.http.delete<void>(
              `${environment.snoots.api.directory}/patients/${patientId}/relatives/${id}`
            )
          )
        )
      ),
      of(undefined)
    );

    const update = iif(
      () => !!payload,
      defer(() =>
        this.http.patch<Pet>(
          `${environment.snoots.api.directory}/patients/${patientId}`,
          payload
        )
      ),
      of(undefined)
    );

    return add.pipe(
      concatMap(() => update),
      concatMap(() => remove)
    );
  }

  @UserNotify({
    successMessage: marker("Pazienti esportati in Excel"),
    errorMessage: marker("Impossibile esportare i pazienti"),
    progressIndeterminate: true,
    progressMessage: marker("Esportazione pazienti in corso")
  })
  exportMassiveExcel(exportOptions: {
    filter?: { tags?: string[] };
    includeIds?: string[];
    excludeIds?: string[];
  }): Observable<void> {
    return this.http.post<void>(
      `${environment.snoots.api.directory}/patients/exports`,
      exportOptions
    );
  }

  loadTags(request: PetLoadTags): Observable<string[]> {
    return this.load({ id: request.id }).pipe(map((pet) => pet.tags || []));
  }

  listProperties(): Observable<string[]> {
    return this.http.get<string[]>(
      `${environment.snoots.api.directory}/properties/patient`
    );
  }

  getByMessageEvent(pet: Pet, message: MessageEvent<Pet>): Pet {
    return {
      ...pet,
      ...message.data
    };
  }

  loadAttachments(
    id: string,
    options?: {
      relatedTo?: string;
      type?: string;
    }
  ): Observable<AttachmentV2[]> {
    return this.http
      .get<AttachmentV2[]>(
        `${environment.snoots.api.directory}/patients/${id}/attachments`,
        {
          params: pickBy(
            {
              relatedTo: options?.relatedTo || "",
              type: options?.type || ""
            },
            (x) => !!x
          )
        }
      )
      .pipe(
        map((attachments: AttachmentV2[]) => {
          return attachments.map((x) => ({
            ...x,
            extension:
              x.name && x.name.toLowerCase().endsWith(".pdf")
                ? "pdf"
                : undefined
          }));
        }),
        map((attachments: AttachmentV2[]) =>
          attachments.sort((left, right) =>
            dateUtil(left.createdAt).isBefore(dateUtil(right.createdAt))
              ? 1
              : -1
          )
        )
      );
  }

  loadAttachmentsByRecovery({
    id,
    recoveryId
  }: {
    id: string;
    recoveryId: string;
  }): Observable<AttachmentV2[]> {
    return this.loadAttachments(id, { relatedTo: recoveryId });
  }

  @UserNotify({
    successMessage: marker("Documento salvato"),
    errorMessage: marker("Non è stato possibile salvare il documento")
  })
  addAttchment({
    id,
    name,
    storageId,
    contentStorageId,
    type,
    relatedTo,
    signable
  }: PatientBackendAddAttachmentParams): Observable<void> {
    return this.http.post<void>(
      `${environment.snoots.api.directory}/patients/${id}/attachments`,
      pickBy({
        name: stringRemoveNonAsciiChar(name),
        storageId,
        contentStorageId,
        type,
        relatedTo,
        signable
      })
    );
  }

  @UserNotify({
    successMessage: marker("Paziente deceduto"),
    errorMessage: marker("Non è stato possibile aggiornare i dati del paziente")
  })
  setDeceased(
    id: string,
    { deceasedAt, deceasedNote }: { deceasedAt: string; deceasedNote: string }
  ): Observable<void> {
    return this.http.put<void>(
      `${environment.snoots.api.directory}/patients/${id}/deceased`,
      {
        deceasedAt,
        deceasedNote
      }
    );
  }

  totalCount(): Observable<number> {
    return this.listLight({ page: 1, pageSize: 1 }).pipe(
      map((response) => response.totalDocs)
    );
  }

  @UserNotify({
    successMessage: marker("Paziente ripristinato"),
    errorMessage: marker("Non è stato possibile ripristinare il paziente")
  })
  resurrect(id: string): Observable<void> {
    return this.http.put<void>(
      `${environment.snoots.api.directory}/patients/${id}/resurrect`,
      {}
    );
  }

  @UserNotify({
    successMessage: marker("Pazienti uniti correttamente"),
    errorMessage: marker("Non è stato possibile unire i pazienti"),
    ignoreErrorCode: [409]
  })
  mergePatients(
    idPatientA: string,
    idPatientB: string,
    maintainedProperties?: string[]
  ): Observable<void> {
    return this.http.put<void>(
      `${environment.snoots.api.directory}/patients/${idPatientA}/merge`,
      {
        patientId: idPatientB,
        maintainedProperties: maintainedProperties
      }
    );
  }
}
